import {
  FREE_QUOTA_SUBMIT_WEB_HOOK_URL,
  SUBMIT_WEB_HOOK_URL,
} from '@/consts/urls';
import { getContext } from '@/context';
import getAuthJsonHeaders from '@/requests/common/getAuthJsonHeaders';

import type { ContentFileAttributes, NotificationConfig } from '@/types';

const QUOTA_EXCEEDED_STATUS = 429;

/**
 * The free-quota webhook is out of free runs for this user. Distinct from a
 * transport rate limit: nobody waits, the generation just costs credits now.
 */
export class FreeQuotaExceededError extends Error {
  constructor() {
    super('Free generation quota exceeded');
    this.name = 'FreeQuotaExceededError';
  }
}

type SubmitWebhookData = {
  response: { id: string };
  status?: string;
  reason?: string;
};

type DriveOptions = {
  folderName: string;
  packageId?: string;
  // See `ContentFileAttributes` / docs/reference/api.md → "Drive attributes workaround".
  attributes?: ContentFileAttributes;
};

type Params = {
  webhookId: string;
  params: Record<string, unknown>;
  driveOptions?: DriveOptions;
  notificationConfig?: NotificationConfig;
  preferFreeQuota?: boolean;
};

/**
 * Submits a flow run. `preferFreeQuota` sends it to the free-quota webhook
 * instead, raising `FreeQuotaExceededError` on 429 so the caller can fall back
 * to the paid endpoint — the server owns the count, we only react to it.
 */
const submitWebHook = async ({
  notificationConfig,
  preferFreeQuota = false,
  ...params
}: Params): Promise<{ id: string }> => {
  const response = await getContext().utils.api.fetch(
    preferFreeQuota ? FREE_QUOTA_SUBMIT_WEB_HOOK_URL : SUBMIT_WEB_HOOK_URL,
    {
      method: 'POST',
      body: JSON.stringify({
        params,
        ...(notificationConfig && { notification: notificationConfig }),
      }),
      headers: getAuthJsonHeaders(),
    }
  );
  if (preferFreeQuota && response.status === QUOTA_EXCEEDED_STATUS) {
    throw new FreeQuotaExceededError();
  }
  if (!response.ok) {
    const body = await response.text().catch(() => '');
    throw new Error(
      `Webhook submission failed: ${body || response.statusText}`
    );
  }
  const data: SubmitWebhookData = await response.json();
  if (!data?.response?.id) {
    throw new Error('Webhook response missing id field');
  }
  return { id: data.response.id };
};

export default submitWebHook;
